import { ReactNode, useState } from 'react';
import cn from 'classnames';
import {
  MdAutoAwesome,
  MdClose,
  MdFilterAlt,
  MdGridView,
  MdKeyboardArrowDown,
  MdNotificationsActive
} from 'react-icons/md';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { CombinedView } from './CombinedView';
import { DiversityView } from './DiversityView';
import { FatigueView } from './FatigueView';
import {
  CsOrange,
  CsRed,
  SUITE_CLIENT,
  SUITE_FILTER_COUNT,
  SUITE_KPI,
  SUITE_PRESET,
  SuiteTab,
  SuiteTabType
} from './creativeSuite-constants';

const ContextPill = ({ label, value }: { label: string; value: string }) => (
  <div className='bp-pill'>
    <span className='lab'>{label}</span>
    <span className='val'>{value}</span>
    <MdKeyboardArrowDown aria-hidden className='chev' />
  </div>
);

const TopBar = () => (
  <div className='bp-topbar'>
    <div className='bp-logo'>
      <span className='bp-logo-mark'>
        <MdAutoAwesome aria-hidden />
      </span>
      Bliss Point <span className='logo-sub'>· Creative Insights</span>
    </div>
    <div className='bp-spacer' />
    <div className='bp-pill filter'>
      <MdFilterAlt aria-hidden />
      <span>Filters</span>
      <span className='ct'>{SUITE_FILTER_COUNT}</span>
    </div>
    <ContextPill label='Client' value={SUITE_CLIENT} />
    <ContextPill label='Preset' value={SUITE_PRESET} />
    <div className='bp-pill kpi'>
      <span className='lab'>KPI</span>
      <span className='val'>{SUITE_KPI}</span>
      <MdKeyboardArrowDown aria-hidden />
    </div>
  </div>
);

const AlertBanner = ({ onDismiss }: { onDismiss: () => void }) => (
  <div className='alert-banner'>
    <div className='ic'>
      <MdNotificationsActive aria-hidden />
    </div>
    <div className='tx'>
      Account dropped to <b>LOW diversity</b> · <b>3 ad sets</b> exceed the fatigue threshold
      (&gt;0.80). Est. <b>$31K/wk</b> fatigued spend.
    </div>
    <span className='src'>From alert · May 31</span>
    <IconButton onClick={onDismiss}>
      <MdClose aria-hidden />
    </IconButton>
  </div>
);

interface TabDef {
  key: SuiteTabType;
  label: string;
  icon: ReactNode;
}

const TABS: TabDef[] = [
  { key: SuiteTab.COMBINED, label: 'Combined view', icon: <MdGridView aria-hidden /> },
  {
    key: SuiteTab.DIVERSITY,
    label: 'Diversity',
    icon: <span className='dot' style={{ background: CsOrange }} />
  },
  {
    key: SuiteTab.FATIGUE,
    label: 'Fatigue',
    icon: <span className='dot' style={{ background: CsRed }} />
  }
];

const SuiteTabs = ({
  active,
  onChange
}: {
  active: SuiteTabType;
  onChange: (tab: SuiteTabType) => void;
}) => (
  <div className='bp-tabs'>
    {TABS.map(tab => (
      <InteractiveDiv
        key={tab.key}
        className={cn('bp-tab', { active: active === tab.key })}
        onClick={() => onChange(tab.key)}
      >
        {tab.icon}
        {tab.label}
      </InteractiveDiv>
    ))}
  </div>
);

const SuiteBody = ({ active }: { active: SuiteTabType }) => {
  if (active === SuiteTab.DIVERSITY) return <DiversityView />;
  if (active === SuiteTab.FATIGUE) return <FatigueView />;
  return <CombinedView />;
};

export const CreativeSuite = () => {
  const [active, setActive] = useState<SuiteTabType>(SuiteTab.COMBINED);
  const [alertVisible, setAlertVisible] = useState(true);

  return (
    <div className='creative-suite'>
      <TopBar />
      {alertVisible && <AlertBanner onDismiss={() => setAlertVisible(false)} />}
      <SuiteTabs active={active} onChange={setActive} />
      <SuiteBody active={active} />
    </div>
  );
};
